import {cdnImage} from '@/util/cdnImage';
import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import {StaticImageData} from 'next/image';
const listCheckIcon = cdnImage('web3/Web3-Best-ListCheck.svg');
import DisplayImage from '@/components/Ui-components/display-image';
import Eyebrow from '@/components/Common/Eyebrow';
import parse from 'html-react-parser';
import {contentMaxWidth, colors, borderRadius, fontSize, fontWeight, letterSpacing, lineHeight, shadows, spacing} from '@/styles/tokens';
import {ICON_TILE_ICON_SIZE, iconTileImageStyle, iconTileSx} from '@/styles/iconTile';

export interface WhatSetsBestFeatureItem {
    icon?: StaticImageData | string;
    iconWidth?: number;
    iconHeight?: number;
    title: string;
    description: string;
}

export interface WhatSetsBestProps {
    heading: string;
    body: string;
    features: WhatSetsBestFeatureItem[];
    /** Optional kicker above the deliverables card heading. Omit it and the card leads with the heading. */
    deliverableEyebrow?: string;
    deliverableHeading: string;
    deliverables: string[];
}

const outerGridSx = {
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', md: '7fr 5fr'},
    gap: {xs: 6, md: 8},
    alignItems: 'start'
} as const;

const leftColSx = {
    display: 'flex',
    flexDirection: 'column',
    gap: 5
} as const;

const headingBlockSx = {
    display: 'flex',
    flexDirection: 'column',
    gap: 3
} as const;

const headingTypoSx = {
    fontSize: {xs: fontSize[32], md: fontSize[48]},
    fontWeight: fontWeight.extrabold,
    color: colors.text.primary,
    letterSpacing: letterSpacing.tight,
    lineHeight: {xs: lineHeight[40], md: lineHeight[56]}
} as const;

const bodyTypoSx = {
    fontSize: fontSize[16],
    fontWeight: fontWeight.regular,
    color: colors.text.primary,
    lineHeight: lineHeight[28],
    maxWidth: contentMaxWidth.narrow
} as const;

const featureGridSx = {
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'},
    gap: 4
} as const;

const featureCardSx = {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing[8],
    p: spacing[24],
    borderRadius: borderRadius.lg,
    bgcolor: 'transparent',
    border: `1px solid ${colors.neutral.borderLight}`,
    transition: 'background 0.2s ease',
    '&:hover': {
        bgcolor: colors.background.default,
        borderColor: colors.primary.main
    }
} as const;

const iconBgSx = iconTileSx;

/** Icon and title share one row; the tile stays fixed while a wrapping title grows beside it. */
const featureHeaderSx = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[12]
} as const;

const featureTitleSx = {
    fontSize: fontSize[18],
    fontWeight: fontWeight.bold,
    color: colors.text.primary,
    lineHeight: lineHeight[28]
} as const;

const featureDescSx = {
    fontSize: fontSize[16],
    fontWeight: fontWeight.regular,
    color: colors.text.primary,
    lineHeight: lineHeight[28]
} as const;

const cardSx = {
    bgcolor: colors.background.tag,
    border: `1px solid ${colors.neutral.borderSubtle}`,
    borderRadius: borderRadius.lg,
    boxShadow: shadows.awardCardElevated,
    overflow: 'hidden',
    p: spacing[32],
    display: 'flex',
    flexDirection: 'column',
    gap: spacing[24]
} as const;

const cardHeadingSx = {
    fontSize: {xs: fontSize[20], md: fontSize[24]},
    fontWeight: fontWeight.bold,
    color: colors.text.primary,
    lineHeight: {xs: lineHeight[28], md: lineHeight[32]}
} as const;

const checklistSx = {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing[16]
} as const;

const checkItemSx = {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[12]
} as const;

const checkIconBoxSx = {
    width: 20,
    height: 22,
    position: 'relative',
    flexShrink: 0
} as const;

const checkTextSx = {
    fontSize: fontSize[16],
    fontWeight: fontWeight.medium,
    color: colors.text.primary,
    lineHeight: lineHeight[20]
} as const;

const WhatSetsBest = ({heading, body, features, deliverableEyebrow, deliverableHeading, deliverables}: WhatSetsBestProps) => {
    return (
        <MUIContainer>
            <Box sx={outerGridSx}>
                {/* Left: heading + feature cards */}
                <Box sx={leftColSx}>
                    <Box sx={headingBlockSx}>
                        <Typography variant="h1" component="h2" className="font-headers" sx={headingTypoSx}>
                            {heading}
                        </Typography>
                        <Typography className="font-regular-text" sx={bodyTypoSx}>
                            {parse(body)}
                        </Typography>
                    </Box>

                    <Box sx={featureGridSx}>
                        {features.map((feature) => (
                            <Box key={feature.title} sx={featureCardSx}>
                                <Box sx={featureHeaderSx}>
                                    {feature.icon && (
                                        <Box sx={iconBgSx}>
                                            <DisplayImage
                                                src={feature.icon as StaticImageData}
                                                alt={feature.title}
                                                width={ICON_TILE_ICON_SIZE}
                                                height={ICON_TILE_ICON_SIZE}
                                                style={iconTileImageStyle}
                                            />
                                        </Box>
                                    )}
                                    <Typography component="h3" className="font-headers" sx={featureTitleSx}>
                                        {feature.title}
                                    </Typography>
                                </Box>
                                <Typography className="font-regular-text remove-underline" sx={featureDescSx}>
                                    {parse(feature.description)}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* Right: deliverables card */}
                <Box sx={cardSx}>
                    {deliverableEyebrow && <Eyebrow text={deliverableEyebrow} sx={{mb: 0}} />}
                    <Typography component="h3" className="font-headers" sx={cardHeadingSx}>
                        {deliverableHeading}
                    </Typography>

                    <Box sx={checklistSx}>
                        {deliverables.map((item) => (
                            <Box key={item} sx={checkItemSx}>
                                <Box sx={checkIconBoxSx}>
                                    <DisplayImage src={listCheckIcon} alt="Included" fill />
                                </Box>
                                <Typography className="font-regular-text" sx={checkTextSx}>
                                    {item}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>
        </MUIContainer>
    );
};

export default memo(WhatSetsBest);
